import { useMantineTheme } from '@mantine/core';
import * as React from 'react';

export type Devices = 'desktop' | 'tablet' | 'phone';

const Context = React.createContext<Devices>('desktop');

type DeviceProviderProps = {
  children?: React.ReactNode;
};

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  const theme = useMantineTheme();

  const phonePx = parseInt(theme.breakpoints.sm);
  const tabletPx = parseInt(theme.breakpoints.md);

  const getCurrentDevice = React.useCallback(() => {
    const width = window.innerWidth;

    if (width <= phonePx) {
      return 'phone';
    } else if (width <= tabletPx) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }, [phonePx, tabletPx]);

  const [device, setDevice] = React.useState<Devices>(getCurrentDevice());

  React.useEffect(() => {
    const handleResize = (): void => {
      setDevice(getCurrentDevice());
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getCurrentDevice]);

  return <Context.Provider value={device}>{children}</Context.Provider>;
};

export const useDevices = (): {
  device: Devices;
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;
} => {
  const currentDevice = React.useContext(Context);

  return React.useMemo(
    () => ({
      device: currentDevice,
      isPhone: currentDevice === 'phone',
      isTablet: currentDevice === 'tablet',
      isDesktop: currentDevice === 'desktop',
    }),
    [currentDevice],
  );
};
